import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { IEmojiData } from 'emoji-picker-react';
import * as React from 'react';
import { toast } from 'sonner';
import { SocialMediaPostViewModel } from '../../../../extViewmodels';
import { useFacebookPageAutoCompleteViewModel } from '../../../../queries/Social/useFacebookPageAutoCompleteViewModel';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import {
	getFacebookMentionRanges,
	getFacebookPageLocationLabel,
	getInstagramCaptionCounts,
	getMentionSegments,
	INSTAGRAM_CAPTION_LIMITS,
} from '../../../../models/utils/socialUtils';
import { ImpersonationContextViewModel } from '../../../../viewmodels/AdminViewModels';
import { Pill } from '../../../components-v2/Pill';
import { TextArea } from '../../../components-v2/TextArea';
import { useEditSocialMediaPostContext } from '../../../containers/socialMedia/EditSocialMediaPost/context';
import { AutoCompleteDropdown } from '../../autocomplete/AutoCompleteDropdown';
import { EmojiPickerButton } from '../../texting/EmojiPickerButton';
import { SocialMediaImageSearchAndDisplay } from '../SocialMediaImageSearchAndDisplay';
import { SocialMediaImagesPreviewModal } from '../SocialMediaImagesPreviewModal';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	headerLabel?: string;
	imagesLabel?: string;
	impersonationContext?: ImpersonationContextViewModel;
	messageLabel?: string;
	onPostScheduled?: (postViewModel: SocialMediaPostViewModel) => void;
	post?: SocialMediaPostViewModel;
	readOnly?: boolean;
	styles?: StyleDeclarationValue[];
}

interface IFacebookMentionTrigger {
	end: number;
	query: string;
	start: number;
}

interface IAnchorPosition {
	left: number;
	top: number;
}

interface IHoveredMention {
	left: number;
	page: Api.IFacebookPageLookupResult;
	top: number;
}

const FACEBOOK_MENTION_TRIGGER_REGEX = /(^|\s)@(\S{0,2048})$/;
const FACEBOOK_VERIFICATION_BADGES = {
	blue_verified: { color: 'blue', label: 'Verified' },
	gray_verified: { color: 'gray', label: 'Verified' },
} as const;

const FacebookVerificationStatusBadge: React.FC<{ verificationStatus?: string }> = ({ verificationStatus }) => {
	const normalizedStatus = verificationStatus?.trim().toLowerCase();
	const badge = normalizedStatus
		? FACEBOOK_VERIFICATION_BADGES[normalizedStatus as keyof typeof FACEBOOK_VERIFICATION_BADGES]
		: undefined;

	return badge ? <Pill color={badge.color} label={badge.label} size='small' variant='label' /> : null;
};

function getFacebookPageMetaLabel(page: Api.IFacebookPageLookupResult) {
	const handleLabel = page.handle ? `@${page.handle}` : '';
	return [handleLabel, getFacebookPageLocationLabel(page.location)].filter(Boolean).join(' · ');
}

function getFacebookMentionTrigger(value: string, caretIndex: number): IFacebookMentionTrigger | null {
	const textBeforeCaret = value.slice(0, caretIndex);
	const triggerMatch = textBeforeCaret.match(FACEBOOK_MENTION_TRIGGER_REGEX);
	if (!triggerMatch) {
		return null;
	}

	const query = triggerMatch[2] || '';
	return {
		end: caretIndex,
		query,
		start: textBeforeCaret.length - query.length - 1,
	};
}

const TEXT_METRIC_PROPERTIES = [
	'borderBottomWidth',
	'borderLeftWidth',
	'borderRightWidth',
	'borderTopWidth',
	'boxSizing',
	'fontFamily',
	'fontSize',
	'fontStyle',
	'fontVariant',
	'fontWeight',
	'letterSpacing',
	'lineHeight',
	'paddingBottom',
	'paddingLeft',
	'paddingRight',
	'paddingTop',
	'textAlign',
	'textIndent',
	'whiteSpace',
	'wordBreak',
	'wordSpacing',
	'wordWrap',
] as const;

function getTextareaAnchorPosition(textArea: HTMLTextAreaElement): IAnchorPosition {
	const computedStyle = window.getComputedStyle(textArea);
	const mirror = document.createElement('div');
	const marker = document.createElement('span');
	const textBeforeCaret = textArea.value.slice(0, textArea.selectionStart || 0);
	const textAfterCaret = textArea.value.slice(textArea.selectionStart || 0);

	mirror.style.position = 'absolute';
	mirror.style.visibility = 'hidden';
	mirror.style.whiteSpace = 'pre-wrap';
	mirror.style.wordWrap = 'break-word';
	mirror.style.overflow = 'hidden';
	mirror.style.width = computedStyle.width;

	TEXT_METRIC_PROPERTIES.forEach(property => {
		mirror.style[property] = computedStyle[property];
	});

	mirror.textContent = textBeforeCaret;
	marker.textContent = textAfterCaret || '.';
	mirror.appendChild(marker);
	textArea.ownerDocument.body.appendChild(mirror);

	const anchorPosition = {
		left: Math.max(12, marker.offsetLeft - textArea.scrollLeft),
		top: Math.max(12, marker.offsetTop - textArea.scrollTop + marker.offsetHeight),
	};

	textArea.ownerDocument.body.removeChild(mirror);
	return anchorPosition;
}

export const SocialMediaPostContentEditor: React.FC<IProps> = props => {
	const {
		className,
		headerLabel,
		imagesLabel,
		impersonationContext,
		messageLabel,
		onPostScheduled,
		post,
		readOnly,
		styles = [],
	} = props;
	const {
		postContent,
		postImages,
		setContent,
		setImages: _setImages,
		hasVideo,
		overlayLogo,
		hideWhiteBackground,
		logoSize,
		setLogoSize,
		facebookMentions,
		setFacebookMentions,
		postTargets,
		selectedCreator,
	} = useEditSocialMediaPostContext();

	const userSession = useUserSession();
	const account = impersonationContext?.account || userSession.account;
	const isFacebookMentioningEnabled = Boolean(
		account?.features?.socialMedia?.enabled &&
			postTargets?.some(target => target.provider === Api.SocialMediaType.Facebook) &&
			!readOnly
	);

	const isInstagramTargeted = Boolean(postTargets?.some(target => target.provider === Api.SocialMediaType.Instagram));
	const isLinkedInTargeted = Boolean(postTargets?.some(target => target.provider === Api.SocialMediaType.LinkedIn));
	const inputRef = React.useRef<HTMLTextAreaElement>(null);
	const textAreaWrapperRef = React.useRef<HTMLDivElement>(null);
	const mentionHighlightRef = React.useRef<HTMLDivElement>(null);
	const [imagePreviewModalOpen, setImagePreviewModalOpen] = React.useState(false);
	const [initialImageUrl, setInitialImageUrl] = React.useState<string>('');
	const [facebookMentionTrigger, setFacebookMentionTrigger] = React.useState<IFacebookMentionTrigger | null>(null);
	const [facebookMentionAnchorPosition, setFacebookMentionAnchorPosition] = React.useState<IAnchorPosition>({
		left: 14,
		top: 36,
	});
	const [hoveredMention, setHoveredMention] = React.useState<IHoveredMention | null>(null);
	const hoveredMentionHideTimeoutRef = React.useRef<number | null>(null);

	const facebookPageAutoComplete = useFacebookPageAutoCompleteViewModel({
		enabled: isFacebookMentioningEnabled && !!selectedCreator?.id,
		forUserId: selectedCreator?.id,
		impersonationContext: impersonationContext?.toJs(),
	});

	const mentionSegments = React.useMemo(
		() => getMentionSegments(postContent, getFacebookMentionRanges(postContent, facebookMentions)),
		[facebookMentions, postContent]
	);
	const hasMentionHighlights = mentionSegments.some(segment => segment.isMention);
	const hasUnlinkedMentions = hasMentionHighlights && (isInstagramTargeted || isLinkedInTargeted);
	const instagramCaptionCounts = React.useMemo(() => getInstagramCaptionCounts(postContent), [postContent]);

	const onInputRef = (ref: HTMLTextAreaElement) => {
		inputRef.current = ref;
	};

	const syncMentionHighlightScroll = React.useCallback(() => {
		if (!mentionHighlightRef.current || !inputRef.current) {
			return;
		}

		mentionHighlightRef.current.scrollTop = inputRef.current.scrollTop;
		mentionHighlightRef.current.scrollLeft = inputRef.current.scrollLeft;
	}, []);

	const cancelHoveredMentionHide = React.useCallback(() => {
		if (hoveredMentionHideTimeoutRef.current === null) {
			return;
		}

		window.clearTimeout(hoveredMentionHideTimeoutRef.current);
		hoveredMentionHideTimeoutRef.current = null;
	}, []);

	// Held open briefly so the pointer can travel from the mention into the bubble's own link.
	const hideHoveredMention = React.useCallback(() => {
		cancelHoveredMentionHide();
		hoveredMentionHideTimeoutRef.current = window.setTimeout(() => setHoveredMention(null), 200);
	}, [cancelHoveredMentionHide]);

	// Hit-test the non-interactive highlight layer through the textarea.
	const onTextAreaMouseMove = React.useCallback(
		(e: React.MouseEvent<HTMLTextAreaElement>) => {
			const highlightLayer = mentionHighlightRef.current;
			const wrapper = textAreaWrapperRef.current;
			if (!highlightLayer || !wrapper) {
				return;
			}

			const wrapperRect = wrapper.getBoundingClientRect();
			const hovered = Array.from(highlightLayer.querySelectorAll<HTMLElement>('[data-mention-segment]'))
				.flatMap(element => Array.from(element.getClientRects()).map(rect => ({ element, rect })))
				.find(
					({ rect }) =>
						e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom
				);

			const segment = hovered ? mentionSegments[Number(hovered.element.dataset.mentionSegment)] : null;
			if (!segment?.isMention) {
				hideHoveredMention();
				return;
			}

			cancelHoveredMentionHide();
			setHoveredMention({
				left: hovered.rect.left - wrapperRect.left,
				page: segment.page,
				top: hovered.rect.bottom - wrapperRect.top + 6,
			});
		},
		[cancelHoveredMentionHide, hideHoveredMention, mentionSegments]
	);

	const syncFacebookMentionTrigger = React.useCallback(
		(content: string, selectionStart?: number | null) => {
			if (
				!isFacebookMentioningEnabled ||
				selectionStart === null ||
				selectionStart === undefined ||
				!inputRef.current
			) {
				setFacebookMentionTrigger(null);
				return;
			}

			const nextTrigger = getFacebookMentionTrigger(content, selectionStart);
			if (!nextTrigger) {
				setFacebookMentionTrigger(null);
				return;
			}

			setFacebookMentionTrigger(nextTrigger);
			setFacebookMentionAnchorPosition(getTextareaAnchorPosition(inputRef.current));
		},
		[isFacebookMentioningEnabled]
	);

	const onTextInputChanged = React.useCallback(
		(e: React.ChangeEvent<HTMLTextAreaElement>) => {
			setContent(e.target.value);
			syncFacebookMentionTrigger(e.target.value, e.target.selectionStart);
		},
		[setContent, syncFacebookMentionTrigger]
	);

	const onTextAreaSelectionChanged = React.useCallback(
		(e: React.KeyboardEvent<HTMLTextAreaElement> | React.MouseEvent<HTMLTextAreaElement>) => {
			if (!facebookMentionTrigger) {
				return;
			}

			const target = e.target as HTMLTextAreaElement;
			syncFacebookMentionTrigger(target.value, target.selectionStart);
		},
		[facebookMentionTrigger, syncFacebookMentionTrigger]
	);

	const onClickEnlargeImage = (initialUrl: string) => {
		setInitialImageUrl(initialUrl);
		setImagePreviewModalOpen(true);
	};

	const onEmojiClick = React.useCallback(
		(_: React.MouseEvent<Element, MouseEvent>, data: IEmojiData) => {
			if (!inputRef.current) {
				return;
			}

			const previousContent = postContent || '';
			const selectionStart = inputRef.current.selectionStart || 0;
			const nextContent =
				previousContent.substring(0, selectionStart) +
				data.emoji +
				previousContent.substring(inputRef.current.selectionStart);
			const nextSelectionStart = selectionStart + data.emoji.length;

			setContent(nextContent);

			window.requestAnimationFrame(() => {
				inputRef.current?.focus();
				inputRef.current?.setSelectionRange(nextSelectionStart, nextSelectionStart);
				syncFacebookMentionTrigger(nextContent, nextSelectionStart);
			});
		},
		[postContent, setContent, syncFacebookMentionTrigger]
	);

	const onFacebookPageSelected = React.useCallback(
		(page: Api.IFacebookPageLookupResult) => {
			if (!facebookMentionTrigger) {
				return;
			}

			const content = postContent || '';
			const start = Math.max(0, Math.min(facebookMentionTrigger.start, content.length));
			const end = Math.max(start, Math.min(facebookMentionTrigger.end, content.length));
			const mentionText = `@${page.pageName || ''}`;
			const nextSelectionStart = start + mentionText.length;

			setContent(content.slice(0, start) + mentionText + content.slice(end));
			setFacebookMentions(currentMentions =>
				(currentMentions || []).some(mention => mention.pageId === page.pageId)
					? currentMentions
					: [...(currentMentions || []), page]
			);
			setFacebookMentionTrigger(null);
			facebookPageAutoComplete.reset();

			window.requestAnimationFrame(() => {
				inputRef.current?.focus();
				inputRef.current?.setSelectionRange(nextSelectionStart, nextSelectionStart);
			});
		},
		[facebookMentionTrigger, facebookPageAutoComplete, postContent, setContent, setFacebookMentions]
	);

	const onValidateSelectedFile = React.useCallback(
		(file: File) => {
			const mimeType = file.type?.toLocaleLowerCase();
			if (!(mimeType.startsWith('video/') || mimeType.startsWith('image/'))) {
				toast.error('Please select a valid image or video file.');
				return false;
			}

			if (mimeType.startsWith('video/') && !hasVideo && postImages?.length > 0) {
				toast.error('This post already has a image. Please remove the image to add a video.');
				return false;
			}

			if (mimeType.startsWith('image/') && hasVideo) {
				toast.error('This post already has a video. Please remove the video to add an image.');
				return false;
			}

			return true;
		},
		[hasVideo, postImages?.length]
	);

	React.useEffect(() => {
		if (isFacebookMentioningEnabled) {
			return;
		}

		setFacebookMentionTrigger(null);
	}, [isFacebookMentioningEnabled]);

	React.useEffect(() => cancelHoveredMentionHide, [cancelHoveredMentionHide]);

	const mentionDropdownSearchQuery = facebookMentionTrigger ? facebookMentionTrigger.query || ' ' : '';
	const normalizedMentionSearch = facebookMentionTrigger?.query?.trim() || '';
	// A Page loaded back from a saved post carries only its id and name; the id alone still links to it.
	const hoveredMentionLink = hoveredMention?.page.link || `https://www.facebook.com/${hoveredMention?.page.pageId}`;

	return (
		<div className={`${css(styleSheet.container, ...styles)} social-media-post-content-editor ${className || ''}`}>
			<div className={css(styleSheet.headerContainer)}>
				{headerLabel && !readOnly && <div className={css(styleSheet.header)}>{headerLabel}</div>}
			</div>
			<div className={css(styleSheet.composerContainer)}>
				<div className={css(styleSheet.labelContainer)}>
					<div className={`emoji-label-container ${css(styleSheet.labelEmoji)}`}>
						{messageLabel && <label>{messageLabel}</label>}
					</div>
					<div className={css(styleSheet.charCount)}>
						<EmojiPickerButton onEmojiClick={!readOnly ? onEmojiClick : null} popoverPlacement={['left', 'top']} />
						{isInstagramTargeted ? (
							<span
								className={css(
									styleSheet.captionCount,
									instagramCaptionCounts.hashtags > INSTAGRAM_CAPTION_LIMITS.hashtags &&
										styleSheet.captionCountOverLimit
								)}
								title='Instagram allows 30 hashtags per caption'
							>
								{`# ${instagramCaptionCounts.hashtags}/${INSTAGRAM_CAPTION_LIMITS.hashtags}`}
							</span>
						) : null}
						{postContent?.length > 0 ? `${postContent?.length}/2200` : '2200'}
					</div>
				</div>
				<div className={css(styleSheet.textAreaWrapper)} ref={textAreaWrapperRef}>
					{hasMentionHighlights ? (
						<div
							aria-hidden='true'
							className={css(styleSheet.mentionHighlightLayer, readOnly && styleSheet.mentionHighlightLayerDisabled)}
							ref={mentionHighlightRef}
						>
							{mentionSegments.map((segment, index) =>
								segment.isMention ? (
									<span
										className={css(styleSheet.mentionHighlight)}
										data-mention-segment={index}
										data-testid='facebook-mention-highlight'
										key={`mention-${index}`}
									>
										{segment.text}
									</span>
								) : (
									<React.Fragment key={`text-${index}`}>{segment.text}</React.Fragment>
								)
							)}
							{'\n'}
						</div>
					) : null}
					<TextArea
						id='social-media-content'
						disabled={readOnly}
						styles={
							hasMentionHighlights
								? [styleSheet.textAreaContainer, styleSheet.textAreaOverHighlights]
								: [styleSheet.textAreaContainer]
						}
						onChange={onTextInputChanged}
						onClick={onTextAreaSelectionChanged}
						onKeyUp={onTextAreaSelectionChanged}
						onMouseLeave={hasMentionHighlights ? hideHoveredMention : undefined}
						onMouseMove={hasMentionHighlights ? onTextAreaMouseMove : undefined}
						onScroll={syncMentionHighlightScroll}
						ref={onInputRef}
						value={postContent || ''}
					/>
					{hoveredMention ? (
						<div
							className={css(styleSheet.mentionBubble)}
							onMouseEnter={cancelHoveredMentionHide}
							onMouseLeave={hideHoveredMention}
							style={{ left: `${hoveredMention.left}px`, top: `${hoveredMention.top}px` }}
						>
							<div className={css(styleSheet.mentionResultNameRow)}>
								<div className={css(styleSheet.mentionResultName)}>{hoveredMention.page.pageName}</div>
								<FacebookVerificationStatusBadge verificationStatus={hoveredMention.page.verificationStatus} />
							</div>
							{getFacebookPageMetaLabel(hoveredMention.page) ? (
								<div className={css(styleSheet.mentionResultMeta)}>{getFacebookPageMetaLabel(hoveredMention.page)}</div>
							) : null}
							<a
								className={css(styleSheet.mentionBubbleLink)}
								href={hoveredMentionLink}
								rel='noopener noreferrer'
								target='_blank'
								title={hoveredMentionLink}
							>
								View page
							</a>
						</div>
					) : null}
					{isFacebookMentioningEnabled && facebookMentionTrigger ? (
						<AutoCompleteDropdown<Api.IFacebookPageLookupResult, Api.IFacebookPageLookupResult>
							anchor={() => (
								<span
									aria-hidden='true'
									className={css(styleSheet.mentionAnchor)}
									style={{
										left: `${facebookMentionAnchorPosition.left}px`,
										top: `${facebookMentionAnchorPosition.top}px`,
									}}
								/>
							)}
							autoCompleteViewModel={facebookPageAutoComplete}
							contentPositionY='bottom'
							dropdownContentItemsStyles={[styleSheet.mentionDropdown]}
							isOpen={true}
							loaderType='small'
							onItemSelected={onFacebookPageSelected}
							onRenderEmptyResults={() => (
								<div className={css(styleSheet.mentionEmptyState)}>
									{normalizedMentionSearch
										? `No Facebook Pages found for @${normalizedMentionSearch}.`
										: 'Type a Facebook Page handle or paste a Facebook Page URL.'}
								</div>
							)}
							onRenderItem={(item, index, highlighted, onClick) => {
								const metaLabel = getFacebookPageMetaLabel(item);
								return (
									<div
										className={css(styleSheet.mentionResultRow, highlighted && styleSheet.mentionResultRowHighlighted)}
										key={`${item.pageId || item.handle || 'facebook-page'}-${index}`}
									>
										<button
											className={css(styleSheet.mentionResultItem)}
											onMouseDown={e => e.preventDefault()}
											type='button'
											onClick={() => onClick()}
										>
											<div className={css(styleSheet.mentionResultNameRow)}>
												<div className={css(styleSheet.mentionResultName)}>{item.pageName}</div>
												<FacebookVerificationStatusBadge verificationStatus={item.verificationStatus} />
											</div>
											{metaLabel ? <div className={css(styleSheet.mentionResultMeta)}>{metaLabel}</div> : null}
										</button>
										{item.link ? (
											<a
												className={css(styleSheet.mentionResultLink)}
												href={item.link}
												onMouseDown={e => e.stopPropagation()}
												rel='noopener noreferrer'
												target='_blank'
												title={item.link}
											>
												View page
											</a>
										) : null}
									</div>
								);
							}}
							searchQuery={mentionDropdownSearchQuery}
							sectionTitleElement={
								<div className={css(styleSheet.mentionSectionTitle)}>Select the Page you want to mention</div>
							}
						/>
					) : null}
				</div>
				{hasUnlinkedMentions ? (
					<div className={css(styleSheet.mentionDisclaimer)}>
						Only Facebook supports Page mentions. Instagram and LinkedIn show the name on its own, without the @, and
						nobody is notified there.
					</div>
				) : null}
			</div>

			<div className={`add-media-container ${css(styleSheet.mediaContainer)}`}>
				<label>{imagesLabel && !readOnly ? imagesLabel : 'Media'}</label>
				<SocialMediaImageSearchAndDisplay
					onClickEnlargeImage={onClickEnlargeImage}
					hasVideo={hasVideo}
					readOnly={readOnly}
					onValidateSelectedFile={onValidateSelectedFile}
					post={post}
					onPostScheduled={onPostScheduled}
				/>
			</div>

			<SocialMediaImagesPreviewModal
				modalProps={{
					isOpen: imagePreviewModalOpen,
					onRequestClose: () => setImagePreviewModalOpen(false),
				}}
				initialImageUrl={initialImageUrl || ''}
				images={postImages}
				showOverlayLogo={overlayLogo}
				logoUrl={account?.preferences?.logo?.url || ''}
				hideWhiteBackground={hideWhiteBackground}
				logoSize={logoSize}
				onLogoSizeChange={setLogoSize}
			/>
		</div>
	);
};
